import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Check, Sparkles, ChevronLeft, ChevronDown, ExternalLink, Mail, User, Phone, Cake, KeyRound, MapPin,
  Hash, CreditCard, UserCheck, Loader2, ArrowRight, ShieldCheck,
  ClipboardCheck, AlertCircle, PlayCircle, Lock, CheckCircle2,
} from "lucide-react";
import { LOGO_URL } from "../lib/brand";
import PartnerAutocomplete from "../components/PartnerAutocomplete";
import { MailDeliveryHint } from "../components/MailDeliveryHint";
import { track } from "../lib/analytics";
import { log } from "../lib/log";
import { BACKEND_URL } from "../lib/apiBase";
import { getAuthHeaders } from "../lib/adminAuth";
import { RF_VIDEO_ID, RF_STEPS } from "../lib/roboforexOnboarding";

const API = `${BACKEND_URL}/api`;

// 10-step checklist mirrors the video script — index-aligned with localStorage progress
const STEPS = [
  {
    text: `Bei RoboForex einloggen und im Dashboard oben links auf "Accounts" und dann "Partneraccount" klicken. (Wenn du noch keinen RoboForex-Account hast, wende dich zwingend an deinen Empfehlungsgeber — er gibt dir den entsprechenden Link für die Registrierung.)`,
    cta: { label: "RoboForex öffnen", href: "https://my.roboforex.com/" },
  },
  `Bei den Benachrichtigungs-Optionen "WhatsApp" auswählen und deine Telefonnummer eingeben.`,
  "Erste zwei Businessplan-Fragen: die vorgegebenen englischen Texte unten 1:1 in die RoboForex-Felder kopieren.",
  `Bei "Anzahl Kunden pro Monat" → "25–50" wählen.`,
  `Alle Compliance-Fragen mit "Nein" beantworten.`,
  "Terms & Conditions akzeptieren und bestätigen, dass du nur erlaubte Aktivitäten durchführst.",
  `"Confirm" klicken, um die Partneraccount-Anfrage abzuschicken.`,
  // Step-order tweak (2026-06): "Auf Freigabe warten" used to sit at
  // position 8 and gave bewerber the impression they had to wait for
  // RoboForex before submitting the Alphalane application. We moved it
  // to the very end so it now reads as a parallel side-track ("send your
  // application now → we review it → in the meantime RoboForex confirms").
  {
    text: (
      <>
        <strong>Zum Schluss:</strong> Bestätige unten auf dieser Seite per Button, dass du alle
        Onboarding-Schritte abgeschlossen hast — du erhältst dann per E-Mail den Link zu deinem
        Partner-Dashboard.{" "}
        <mark className="bg-yellow-200 text-slate-900 font-semibold px-1 rounded">
          Sobald RoboForex bestätigt hat (3–5 Werktage), trägst du dort unter „Profil" deinen
          Affiliate-Code, deine Partner-Account-Nummer und deinen Reflink nach — erst dann ist dein
          persönlicher Empfehlungslink live.
        </mark>
      </>
    ),
    image: {
      src: "https://customer-assets.emergentagent.com/job_web-design-builder-3/artifacts/p7dwf32z_IMG_9098.jpeg",
      alt: "Beispiel: Partner-Account-Nummer im RoboForex Dashboard (z. B. 30296126-ProCent Partner)",
      caption: 'So findest du deine Partner-Account-Nummer im RoboForex-Dashboard (Bereich „Accounts → Partneraccount").',
    },
  },
];

const BUSINESS_PLAN_TEXTS = [
  {
    label: "Frage 1 · Please describe your business approach in detail",
    text: "I introduce automated Copy Trading to individuals within my personal network who approach me on their own initiative. I do not provide investment advice, trading signals, financial planning, or portfolio management services, and I have no access to any client accounts.\n\nAll decisions regarding whether, how, and where to trade are made entirely and independently by each individual. My activity is limited exclusively to warm personal referrals through private conversations and existing personal contacts. I do not conduct public advertising campaigns, social media promotions, websites, paid marketing, or mass outreach activities.",
  },
  {
    label: "Frage 2 · From which countries are you going to attract clients?",
    text: "Switzerland (non-EU) exclusively. All contacts approach me on their own initiative within my personal network. I will not conduct any promotion targeting EU, EEA, or UK residents, and I will not use any EU/EEA/UK local language sources or channels.",
  },
];

const STORAGE_KEY = "alphalane_partner_onboarding_checks";
// Separate progress key for the "Noch kein RoboForex-Konto?" accordion —
// mirrors the customer-onboarding RoboForex account-opening walkthrough but
// scoped to the partner-onboarding page so the two don't interfere.
const RF_STORAGE_KEY = "alphalane_partner_onboarding_rf_account_checks";
// Tracks whether the user has already submitted their application from
// THIS browser so we can show a "Bewerbung eingegangen ✅" inline banner
// on Step 9 even after a reload (the full-page success state is one-shot
// and gone after a manual page refresh).
const SUBMITTED_KEY = "alphalane_partner_signup_submitted_at";
const SUBMITTED_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 Tage

export default function PartnerOnboarding() {
  const { partnerSlug } = useParams();
  const [checks, setChecks] = useState(() => Array(STEPS.length).fill(false));
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    birthdate: "",
    affiliate_code: "",
    roboforex_account: "",
    callmebot_apikey: "",
    referrer_name: "",
    referrer_partner_id: null,
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  // True if this browser submitted the application in the last 14 days.
  // Drives the inline green banner on Step 9.
  const [recentlySubmitted, setRecentlySubmitted] = useState(false);
  const [err, setErr] = useState("");
  const formRef = useRef(null);

  // ── "Noch kein RoboForex-Konto?" accordion (mirrors customer-onboarding) ─
  const [rfOpen, setRfOpen] = useState(false);
  const [rfChecks, setRfChecks] = useState(() => Array(RF_STEPS.length).fill(false));

  // ── Step 11 (optional) — RoboForex existing-account linking ──────────
  // Only relevant for the (rare) bewerber who already had a RoboForex
  // account before Alphalane. The block stays collapsed unless they tick
  // it; on tick we render the full "open ticket at my.roboforex.com/help"
  // walkthrough plus pre-filled subject + body with the sponsor's data
  // already substituted.
  const [hadPriorRoboforex, setHadPriorRoboforex] = useState(false);
  const [linkPartnerAccountNumber, setLinkPartnerAccountNumber] = useState("");

  // ── Login-Gate: Seite nur für freigeschaltete (eingeloggte) Partner ──
  const [me, setMe] = useState(null); // null=loading, false=nicht eingeloggt, object=Partner
  const [confirming, setConfirming] = useState(false);
  const [confirmedAt, setConfirmedAt] = useState(null);
  useEffect(() => {
    // Session liegt im httpOnly-Cookie; localStorage hält nur einen Marker.
    const marker = localStorage.getItem("alphalane_partner_token");
    if (!marker) {
      setMe(false);
      return;
    }
    axios.get(`${API}/partner/me`, { withCredentials: true, headers: getAuthHeaders() })
      .then((r) => {
        setMe(r.data);
        setConfirmedAt(r.data.onboarding_confirmed_at || null);
      })
      .catch(() => setMe(false));
  }, []);

  const confirmOnboarding = async () => {
    setConfirming(true);
    try {
      const r = await axios.post(`${API}/partner/onboarding/confirm`, {}, { withCredentials: true, headers: getAuthHeaders() });
      setConfirmedAt(r.data.confirmed_at);
      if (r.data.already_confirmed) {
        toast.info("Dein Onboarding war bereits bestätigt.");
      } else {
        toast.success("Onboarding bestätigt! Die E-Mail mit deinem Dashboard-Link ist unterwegs. 🎉");
      }
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    } catch {
      toast.error("Bestätigung fehlgeschlagen — bitte neu einloggen und nochmal versuchen.");
    } finally {
      setConfirming(false);
    }
  };

  // Hydrate progress from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length === STEPS.length) {
          setChecks(parsed);
        }
      }
      const rfRaw = localStorage.getItem(RF_STORAGE_KEY);
      if (rfRaw) {
        const parsed = JSON.parse(rfRaw);
        if (Array.isArray(parsed) && parsed.length === RF_STEPS.length) {
          setRfChecks(parsed);
          // Auto-expand if user already started ticking — preserves their state on reload.
          if (parsed.some(Boolean)) setRfOpen(true);
        }
      }
      const submittedAt = parseInt(localStorage.getItem(SUBMITTED_KEY) || "0", 10);
      if (submittedAt && Date.now() - submittedAt < SUBMITTED_TTL_MS) {
        setRecentlySubmitted(true);
      }
    } catch (err) {
      log.warn("[partner-onboarding] localStorage read failed:", err);
    }
    document.title = "Partner-Onboarding · Alphalane";
  }, []);

  // Pre-fill referrer if /partneronboarding/:slug — uses the dedicated
  // `/auth/partner/{slug}` lookup which matches slug, ref_link, AND the
  // RoboForex affiliate code (e.g. /partneronboarding/zbtxc).
  const [referrerLocked, setReferrerLocked] = useState(false);
  // Sponsor's RoboForex details — auto-filled into the Step 11
  // RoboForex "link partner account" ticket template so the new partner
  // doesn't have to type the sponsor's account ID + affiliate code.
  const [sponsorMeta, setSponsorMeta] = useState({ name: "", accountId: "", affiliateCode: "" });
  useEffect(() => {
    if (!partnerSlug) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/partner/${encodeURIComponent(partnerSlug)}`);
        if (r.data && r.data.name) {
          setForm((f) => ({
            ...f,
            referrer_name: r.data.name,
            referrer_partner_id: r.data.id || null,
          }));
          setReferrerLocked(true);
          setSponsorMeta({
            name: r.data.name || "",
            accountId: r.data.roboforex_partneraccount || "",
            affiliateCode: r.data.affiliate_code || partnerSlug || "",
          });
        }
      } catch (err) {
        log.debug("[partner-onboarding] referrer lookup silent skip:", err);
      }
    })();
  }, [partnerSlug]);

  // Save check changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(checks));
    } catch (err) {
      log.warn("[partner-onboarding] localStorage write failed:", err);
    }
  }, [checks]);

  // Persist RoboForex-account accordion checks separately
  useEffect(() => {
    try {
      localStorage.setItem(RF_STORAGE_KEY, JSON.stringify(rfChecks));
    } catch (err) {
      log.warn("[partner-onboarding] rf-checks localStorage write failed:", err);
    }
  }, [rfChecks]);

  const toggleRf = (i) => () => {
    setRfChecks((prev) => prev.map((v, idx) => (idx === i ? !v : v)));
  };
  const rfCompleted = rfChecks.filter(Boolean).length;

  const completed = checks.filter(Boolean).length;
  const percent = Math.round((completed / STEPS.length) * 100);

  const motivation = useMemo(() => {
    if (done) return "Onboarding bestätigt — du bist startklar! 🎉";
    if (percent === 100) return "Alle Schritte geschafft — jetzt unten dein Onboarding bestätigen.";
    if (percent >= 80) return "Fast geschafft — bleib dran!";
    if (percent >= 50) return "Halbzeit — du machst das super.";
    if (percent >= 20) return "Schon gut unterwegs — weiter so!";
    if (percent > 0) return "Erster Schritt gemacht. Auf geht's!";
    return "Bereit? Schau das Video an und hake jeden Schritt einzeln ab.";
  }, [percent, done]);

  const toggle = (i) => () => {
    setChecks((prev) => prev.map((v, idx) => (idx === i ? !v : v)));
  };

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const copyText = async (text, label = "Text") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} kopiert!`);
    } catch (err) {
      log.warn("[partner-onboarding] clipboard write failed:", err);
      toast.error("Konnte nicht kopiert werden.");
    }
  };

  if (me === null) {
    return (
      <div className="min-h-screen bg-[var(--al-bg)]">
        <Header />
        <div className="flex items-center justify-center py-32 text-slate-500 text-sm" data-testid="partner-onboarding-loading">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade…
        </div>
      </div>
    );
  }

  if (me === false) {
    return (
      <div className="min-h-screen bg-[var(--al-bg)]">
        <Header />
        <main className="max-w-lg mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <div className="al-card p-8 sm:p-10 text-center" data-testid="partner-onboarding-login-wall">
            <div className="mx-auto h-14 w-14 rounded-full bg-violet-100 border border-violet-200 flex items-center justify-center mb-5">
              <Lock className="h-6 w-6 text-[var(--al-purple)]" />
            </div>
            <h1 className="font-display font-bold text-2xl sm:text-3xl text-slate-900">Partner-Onboarding</h1>
            <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed">
              Diese Seite ist für <strong>freigeschaltete Alphalane-Partner</strong>.
              Logge dich mit den Zugangsdaten aus deiner Annahme-Mail
              („✅ Deine Alphalane Partner-Bewerbung wurde angenommen") ein.
            </p>
            <a href="/partner/login?next=%2Fpartneronboarding" data-testid="onboarding-login-cta" className="al-cta mt-6 w-full justify-center">
              Zum Partner-Login <ArrowRight className="h-4 w-4" />
            </a>
            <div className="mt-7 rounded-xl border border-violet-200 bg-violet-50/60 p-4 text-sm text-slate-700 text-left" data-testid="onboarding-not-applied-hint">
              <strong className="text-slate-900">Noch kein Partner?</strong> Reiche zuerst deine Bewerbung ein —
              nach der Freischaltung erhältst du deine Zugangsdaten und den Link zu dieser Seite per E-Mail.
              <div className="mt-3">
                <Link to={partnerSlug ? `/bewerbung/${partnerSlug}` : "/bewerbung"} data-testid="onboarding-apply-link" className="al-cta-ghost text-sm inline-flex">
                  Zur Partner-Bewerbung <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--al-bg)]" data-testid="partner-onboarding-page">
      <Header />

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        {/* Hero + progress */}
        <section className="mb-10" data-testid="partner-onboarding-hero">
          <span className="al-tag"><Sparkles className="h-3 w-3" /> Partner-Onboarding</span>
          <h1 className="mt-3 font-display font-bold text-3xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            So wirst du <span className="al-grad-text">Empfehlungs-Partner.</span>
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
            Willkommen im Alphalane-Partner-Programm! Schau dir das kurze Erklärvideo an,
            hake jeden Schritt einzeln ab und <strong>bestätige am Ende dein Onboarding</strong> —
            danach erhältst du per E-Mail den Link zu deinem Partner-Dashboard.
          </p>

          {/* Progress */}
          <div className="mt-7 al-card p-5 sm:p-6" data-testid="partner-onboarding-progress">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">
                Dein Fortschritt
              </div>
              <div className="font-mono-tab text-sm font-bold text-[var(--al-purple)]">
                {completed} / {STEPS.length} · {percent}%
              </div>
            </div>
            <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full al-grad-bg transition-all duration-500"
                style={{ width: `${percent}%` }}
              />
            </div>
            <p className="mt-3 text-sm text-slate-600">{motivation}</p>
          </div>
        </section>

        {/* Video — YouTube embed */}
        <section className="mb-10 al-card overflow-hidden" data-testid="partner-onboarding-video">
          <div className="aspect-video bg-black relative">
            <iframe
              className="absolute inset-0 w-full h-full"
              src="https://www.youtube-nocookie.com/embed/HR-mryQkztM?rel=0&modestbranding=1"
              title="Alphalane Partner-Onboarding — RoboForex Partneraccount erstellen"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              data-testid="partner-onboarding-video-iframe"
            />
          </div>
        </section>

        {/* "Noch kein RoboForex-Konto?" — collapsible walkthrough, identical
            content to the customer-onboarding RoboForex account-opening flow.
            Only shown for bewerber who haven't opened a private RoboForex
            account yet (Partner-Account requires a private one first). */}
        <section className="mb-10" data-testid="partner-rf-account-accordion">
          <button
            type="button"
            onClick={() => setRfOpen((v) => !v)}
            aria-expanded={rfOpen}
            data-testid="partner-rf-accordion-toggle"
            className={`w-full al-card overflow-hidden text-left transition-all ${
              rfOpen ? "ring-2 ring-violet-300" : "hover:border-violet-300"
            }`}
          >
            <div className="px-5 sm:px-7 py-5 flex items-center gap-4">
              <div className="shrink-0 h-11 w-11 rounded-2xl al-grad-bg text-white flex items-center justify-center shadow-md shadow-violet-200">
                <PlayCircle className="h-6 w-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-base sm:text-lg leading-tight text-slate-900">
                  Noch kein RoboForex-Konto?
                </div>
                <div className="mt-1 text-xs sm:text-sm text-slate-600 leading-snug">
                  Klick hier für die komplette Schritt-für-Schritt-Anleitung inkl. Video — identisch zum Kunden-Onboarding.
                </div>
                {rfCompleted > 0 && (
                  <div className="mt-1.5 text-[11px] font-mono-tab font-bold text-emerald-600">
                    {rfCompleted} / {RF_STEPS.length} erledigt
                  </div>
                )}
              </div>
              <ChevronDown
                className={`h-5 w-5 text-slate-400 transition-transform shrink-0 ${rfOpen ? "rotate-180 text-[var(--al-purple)]" : ""}`}
              />
            </div>
          </button>

          {rfOpen && (
            <div className="mt-3" data-testid="partner-rf-accordion-body">
              <div className="al-card overflow-hidden">
                <div className="aspect-video bg-black relative">
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    src={`https://www.youtube-nocookie.com/embed/${RF_VIDEO_ID}?rel=0&modestbranding=1`}
                    title="RoboForex Account-Eröffnung — Schritt für Schritt"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    data-testid="partner-rf-video-iframe"
                  />
                </div>
              </div>

              {partnerSlug && (
                <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900 flex items-start gap-3" data-testid="partner-rf-partner-id-hint">
                  <Sparkles className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <div>
                    Bei der Registrierung als Partner-ID eingeben: <strong className="font-mono-tab text-base">{partnerSlug}</strong>
                  </div>
                </div>
              )}

              <ul className="mt-4 al-card divide-y divide-[var(--al-line)] overflow-hidden" data-testid="partner-rf-checklist">
                {RF_STEPS.map((step, i) => {
                  const item = typeof step === "string" ? { text: step } : step;
                  const checked = rfChecks[i];
                  return (
                    <li key={`rf-step-${i}`} data-testid={`partner-rf-step-${i + 1}`}>
                      <button
                        type="button"
                        onClick={toggleRf(i)}
                        data-testid={`partner-rf-step-check-${i + 1}`}
                        className={`w-full text-left px-5 py-4 flex items-start gap-4 transition-all ${checked ? "bg-emerald-50/40" : "hover:bg-slate-50"}`}
                      >
                        <div className={`flex-shrink-0 mt-0.5 h-6 w-6 rounded-md border-2 flex items-center justify-center transition-all ${
                          checked ? "bg-emerald-500 border-emerald-500" : "border-slate-300"
                        }`}>
                          {checked && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] font-mono-tab font-bold text-slate-400">{String(i + 1).padStart(2, "0")}</div>
                          <div className={`mt-0.5 text-sm sm:text-[15px] leading-relaxed ${checked ? "text-slate-400 line-through" : "text-slate-700"}`}>
                            {item.text}
                          </div>
                        </div>
                      </button>
                      {item.cta && !checked && (
                        <div className="px-5 pb-4 pl-[60px]">
                          <a
                            href={item.cta.href}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`partner-rf-step-cta-${i + 1}`}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full al-grad-bg text-white text-sm font-display font-semibold hover:opacity-90 transition-opacity shadow-sm"
                          >
                            <ExternalLink className="h-4 w-4" /> {item.cta.label}
                          </a>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>

              <p className="mt-4 text-xs text-slate-500 leading-relaxed">
                Sobald dein privates RoboForex-Konto verifiziert und finanziert ist, kannst du oben mit der Schritt-für-Schritt-Anleitung zur Eröffnung deines <strong>Partner-Accounts</strong> fortfahren.
              </p>
            </div>
          )}
        </section>

        {/* Checklist */}
        <section className="mb-10 al-card p-6 sm:p-8" data-testid="partner-onboarding-checklist">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="h-4 w-4 text-[var(--al-purple)]" />
            <h2 className="font-display font-bold text-lg">Schritt-für-Schritt-Anleitung</h2>
          </div>
          <p className="text-sm text-slate-600 mb-5">
            Hake jeden Schritt nach erledigter Aktion ab. Dein Fortschritt bleibt lokal gespeichert.
          </p>
          <ul className="space-y-2.5">
            {STEPS.map((s, i) => {
              const item = typeof s === "string" ? { text: s } : s;
              const checked = checks[i];
              return (
                <Fragment key={`step-${i}`}>
                  {/* Show a green confirmation banner ABOVE step 9 (the
                      "Wir prüfen deine Bewerbung" step) once the user has
                      already submitted from this browser within the last
                      14 days. Removes the doubt after returning to the
                      page post-submit. */}
                  {i === 8 && recentlySubmitted && (
                    <li className="list-none" data-testid="submitted-banner">
                      <div className="ml-1 sm:ml-9 rounded-xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-white p-4 my-2">
                        <div className="flex items-start gap-3">
                          <div className="shrink-0 h-9 w-9 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-sm">
                            <Check className="h-5 w-5" />
                          </div>
                          <div className="flex-1">
                            <div className="font-display font-bold text-sm sm:text-base text-emerald-900">
                              Bewerbung eingegangen — wir prüfen jetzt.
                            </div>
                            <p className="mt-1 text-xs sm:text-sm text-emerald-800/90 leading-relaxed">
                              Pascal schaut sich deine Daten innerhalb von 1–2 Werktagen an.
                              Du bekommst die Annahme-Mail mit Betreff
                              „✅ Deine Alphalane Partner-Bewerbung wurde angenommen"
                              direkt in dein Postfach. Du musst hier nichts mehr tun.
                            </p>
                          </div>
                        </div>
                      </div>
                    </li>
                  )}
                  <li data-testid={`step-${i + 1}`}>
                    <label className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 hover:border-violet-200 hover:bg-violet-50/30 cursor-pointer transition-all">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={toggle(i)}
                        data-testid={`step-check-${i + 1}`}
                        className="mt-0.5 h-5 w-5 rounded-md border-slate-300 text-[var(--al-purple)] focus:ring-violet-300"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="font-mono-tab text-xs text-slate-400 font-bold">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <span className={`text-sm sm:text-[15px] leading-snug ${checked ? "line-through text-slate-400" : "text-slate-800"}`}>
                            {item.text}
                          </span>
                        </div>
                        {item.cta && !checked && (
                        <a
                          href={item.cta.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`step-cta-${i + 1}`}
                          className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-[var(--al-purple)] hover:underline"
                        >
                          {item.cta.label} <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                      {item.image && (
                        <figure
                          data-testid={`step-image-${i + 1}`}
                          className="mt-3 rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50/60 to-white p-3"
                        >
                          <img
                            src={item.image.src}
                            alt={item.image.alt}
                            loading="lazy"
                            className="block w-full max-w-md mx-auto rounded-lg border border-slate-200 bg-white"
                          />
                          {item.image.caption && (
                            <figcaption className="mt-2 text-[11px] sm:text-xs text-slate-600 text-center leading-snug">
                              {item.image.caption}
                            </figcaption>
                          )}
                        </figure>
                      )}
                    </div>
                  </label>
                </li>
                {/* Render the two business-plan texts right between step 3 and step 4. */}
                {i === 2 && (
                  <li className="list-none" data-testid="bizplan-inline">
                    <div className="ml-1 sm:ml-9 rounded-xl border-2 border-violet-200 bg-gradient-to-br from-violet-50/80 to-white p-5 my-2">
                      <div className="flex items-center gap-2 mb-2">
                        <ClipboardCheck className="h-4 w-4 text-[var(--al-purple)]" />
                        <h3 className="font-display font-bold text-sm sm:text-base">
                          Diese englischen Texte 1:1 bei den ersten zwei Fragen einfügen
                        </h3>
                      </div>
                      <p className="text-xs text-slate-600 mb-4 leading-relaxed">
                        Sind so formuliert, dass RoboForex sie problemlos akzeptiert. Klick „Text kopieren" und füge ihn drüben ein.
                      </p>
                      <div className="space-y-3">
                        {BUSINESS_PLAN_TEXTS.map((bp, bpi) => (
                          <div key={bp.label || `bp-${bpi}`} className="rounded-xl border border-slate-200 bg-white p-4">
                            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                              <div className="text-[11px] uppercase tracking-widest font-bold text-[var(--al-purple)] leading-snug">
                                {bp.label}
                              </div>
                              <button
                                type="button"
                                onClick={() => copyText(bp.text, bp.label)}
                                data-testid={`bizplan-copy-${bpi}`}
                                className="text-xs font-bold text-white bg-[var(--al-purple)] hover:opacity-90 rounded-full px-3 py-1.5 inline-flex items-center gap-1 shrink-0"
                              >
                                <Hash className="h-3 w-3" /> Text kopieren
                              </button>
                            </div>
                            <p className="text-[13px] sm:text-sm text-slate-700 leading-relaxed whitespace-pre-line">{bp.text}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </li>
                )}
                </Fragment>
              );
            })}
          </ul>

          <button
            type="button"
            onClick={() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
            data-testid="jump-to-form"
            className="al-cta mt-7 w-full justify-center"
          >
            Weiter: Onboarding abschliessen <ArrowRight className="h-4 w-4" />
          </button>

          {/* ── Optional: bestehender RoboForex-Account ────────────────────
              Nur relevant, wenn der Bewerber schon vor Alphalane ein
              RoboForex-Konto hatte. Bleibt eingeklappt, bis er aktiv
              den Haken setzt — dann öffnet sich die volle Anleitung
              mit pre-filled Subject + Body. Bewusst NACH dem Bewerbungs-
              CTA-Button platziert, damit Bewerber ohne Alt-Konto zuerst
              die Bewerbung sehen und nicht durch das Amber-Feld irritiert
              werden. */}
          <div className="mt-5" data-testid="step-11-existing-roboforex">
            <label className="flex items-start gap-3 p-3 rounded-xl border-2 border-amber-300 bg-amber-50/40 hover:bg-amber-50 cursor-pointer transition-all">
              <input
                type="checkbox"
                checked={hadPriorRoboforex}
                onChange={(e) => setHadPriorRoboforex(e.target.checked)}
                data-testid="step-check-11"
                className="mt-0.5 h-5 w-5 rounded-md border-amber-400 text-amber-600 focus:ring-amber-300"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-sm sm:text-[15px] leading-snug text-slate-800 font-semibold">
                    Ich hatte bereits <span className="underline decoration-amber-400 decoration-2 underline-offset-2">vor Alphalane</span> ein RoboForex-Konto
                  </span>
                </div>
                <p className="mt-1 text-[12px] sm:text-xs text-amber-800/90 leading-snug">
                  Nur ankreuzen wenn das auf dich zutrifft — sonst kannst du diesen Schritt überspringen. Falls ja, brauchen wir noch einen kleinen Zusatzschritt damit RoboForex deinen Partner-Account korrekt mit deinem Empfehlungsgeber verknüpft.
                </p>
              </div>
            </label>

            {hadPriorRoboforex && (
              <div
                data-testid="step-11-instructions"
                className="mt-3 ml-1 sm:ml-9 rounded-xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-white p-5"
              >
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="h-4 w-4 text-amber-700" />
                  <h3 className="font-display font-bold text-sm sm:text-base text-amber-900">
                    Verknüpfungs-Ticket bei RoboForex eröffnen
                  </h3>
                </div>

                <ol className="space-y-3 text-[13px] sm:text-sm text-slate-800 leading-relaxed list-decimal list-inside">
                  <li>
                    <strong>Warte zuerst</strong>, bis dein <strong>Partner-Account von RoboForex bestätigt</strong> wurde
                    (Mail mit Betreff <em>„You have successfully registered an account at RoboForex"</em>).
                    Vorher kann RoboForex die Verknüpfung nicht vornehmen.
                  </li>
                  <li>
                    Öffne danach die Ticket-Seite:{" "}
                    <a
                      href="https://my.roboforex.com/en/help/add-a-ticket/"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="step-11-ticket-link"
                      className="font-bold text-[var(--al-purple)] hover:underline inline-flex items-center gap-1"
                    >
                      my.roboforex.com/en/help/add-a-ticket/
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </li>
                  <li>
                    Wähle <strong>Category</strong>: <em>Partner Department</em> · <strong>Priority</strong>: <em>Normal</em> · <strong>Type</strong>: <em>Question</em>.
                  </li>
                  <li>Trage unten deine Partner-Account-Nummer ein — wir bauen dir den Text dann automatisch fertig zum Kopieren.</li>
                </ol>

                <figure className="mt-4 rounded-xl border border-amber-200 bg-white p-3">
                  <img
                    src="https://customer-assets.emergentagent.com/job_web-design-builder-3/artifacts/dwzn40sr_IMG_9801.jpeg"
                    alt="RoboForex Ticket-Formular — Partner Department, Subject, Account number, Question"
                    loading="lazy"
                    data-testid="step-11-screenshot"
                    className="block w-full max-w-md mx-auto rounded-lg border border-slate-200"
                  />
                  <figcaption className="mt-2 text-[11px] sm:text-xs text-slate-600 text-center leading-snug">
                    So sieht das Ticket-Formular auf my.roboforex.com aus — fülle die markierten Felder genau so aus.
                  </figcaption>
                </figure>

                {/* Partner account number input */}
                <div className="mt-5">
                  <label className="block text-[11px] uppercase tracking-widest font-bold text-amber-800 mb-1.5">
                    Deine RoboForex Partner-Account-Nummer
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={linkPartnerAccountNumber}
                    onChange={(e) => setLinkPartnerAccountNumber(e.target.value.replace(/\D/g, ""))}
                    placeholder="z. B. 37348001"
                    data-testid="step-11-account-input"
                    className="w-full px-3 py-2.5 rounded-lg border border-amber-300 bg-white text-sm focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none font-mono"
                  />
                  <p className="mt-1 text-[11px] text-slate-500">
                    Die 8-stellige Nummer aus dem RoboForex-Dashboard → <em>Accounts → Partneraccount</em>.
                  </p>
                </div>

                {/* Subject — copy block */}
                <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                    <div className="text-[11px] uppercase tracking-widest font-bold text-[var(--al-purple)]">
                      Subject (Betreff)
                    </div>
                    <button
                      type="button"
                      onClick={() => copyText("Request to Link Partner Account to Existing Affiliate Structure", "Subject")}
                      data-testid="step-11-copy-subject"
                      className="al-cta-ghost text-[11px] !px-3 !py-1.5"
                    >
                      <ClipboardCheck className="h-3.5 w-3.5" /> Subject kopieren
                    </button>
                  </div>
                  <p className="text-[13px] font-mono text-slate-800 bg-slate-50 rounded-md px-3 py-2 select-all">
                    Request to Link Partner Account to Existing Affiliate Structure
                  </p>
                </div>

                {/* Body — copy block with sponsor data filled in */}
                <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                    <div className="text-[11px] uppercase tracking-widest font-bold text-[var(--al-purple)]">
                      Question (Nachrichtentext)
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const accNum = linkPartnerAccountNumber.trim() || "[ACCOUNT NUMBER]";
                        // Default fallback (no partner slug → no sponsor record loaded) =
                        // Moonshine Solutions GmbH master account, so the new partner
                        // can submit the ticket even without coming via a personal reflink.
                        const sponsorName = sponsorMeta.name || "Moonshine Solutions GmbH";
                        const sponsorAcc = sponsorMeta.accountId || "30296126";
                        const sponsorAff = sponsorMeta.affiliateCode || "gubnr";
                        const body = `Dear RoboForex Team,

I would like to request that the partner account with the number ${accNum} be linked to the affiliate structure of:

Primary Partner Details:
* Name: ${sponsorName}
* Account ID: ${sponsorAcc}
* Affiliate Code: ${sponsorAff}

Please connect the above-mentioned partner account to this existing partner structure and confirm once the process has been completed.

Should you require any additional information or documentation, please let me know.

Thank you for your assistance.

Kind regards`;
                        copyText(body, "Ticket-Text");
                      }}
                      disabled={!linkPartnerAccountNumber}
                      data-testid="step-11-copy-body"
                      className="al-cta text-[11px] !px-3 !py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ClipboardCheck className="h-3.5 w-3.5" /> Text kopieren
                    </button>
                  </div>
                  {!linkPartnerAccountNumber && (
                    <p className="text-[11px] text-amber-700 mb-2 italic">
                      Trag erst deine Partner-Account-Nummer oben ein, dann kannst du den Text kopieren.
                    </p>
                  )}
                  <pre className="text-[12.5px] sm:text-[13px] font-mono text-slate-800 bg-slate-50 rounded-md px-3 py-3 whitespace-pre-wrap leading-relaxed select-all">
{`Dear RoboForex Team,

I would like to request that the partner account with the number `}<strong className="text-amber-700">{linkPartnerAccountNumber || "[ACCOUNT NUMBER]"}</strong>{` be linked to the affiliate structure of:

Primary Partner Details:
* Name: `}<strong className="text-violet-700">{sponsorMeta.name || "Moonshine Solutions GmbH"}</strong>{`
* Account ID: `}<strong className="text-violet-700">{sponsorMeta.accountId || "30296126"}</strong>{`
* Affiliate Code: `}<strong className="text-violet-700">{sponsorMeta.affiliateCode || "gubnr"}</strong>{`

Please connect the above-mentioned partner account to this existing partner structure and confirm once the process has been completed.

Should you require any additional information or documentation, please let me know.

Thank you for your assistance.

Kind regards`}
                  </pre>
                </div>

                <p className="mt-4 text-[12px] text-slate-600 italic">
                  Nach Absenden des Tickets bestätigt dir der RoboForex-Partner-Support die Verknüpfung meist innert 1–2 Werktagen.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Daten nachtragen — Info für später */}
        <section className="al-card p-6 sm:p-8" ref={formRef} data-testid="partner-onboarding-next">
          <div className="flex items-center gap-2 mb-1">
            <UserCheck className="h-4 w-4 text-[var(--al-purple)]" />
            <h2 className="font-display font-bold text-lg">Später: Deine RoboForex-Daten nachtragen</h2>
          </div>
          <p className="text-sm text-slate-600 mb-5">
            Sobald RoboForex deinen Partneraccount bestätigt hat (3–5 Werktage), trägst du
            <strong> Affiliate-Code (Agent ID)</strong>, <strong>Partner-Account-Nummer</strong> und{" "}
            <strong>Reflink</strong> in deinem Partner-Dashboard unter <strong>„Profil"</strong> ein —
            erst dann ist dein persönlicher Empfehlungslink live und Provisionen werden dir automatisch zugeordnet.
            Den Dashboard-Link erhältst du gleich per E-Mail, sobald du unten dein Onboarding bestätigst.
          </p>
        </section>

        {/* ── Nach dem Absenden — Schritte 09 / 10 / 11 ─────────────── */}
        <section className="al-card p-6 sm:p-8 mt-8 border-emerald-200 bg-gradient-to-br from-emerald-50/40 via-white to-slate-50/40" data-testid="post-submit-steps">
          <div className="flex items-center gap-2 mb-1">
            <ArrowRight className="h-4 w-4 text-emerald-600" />
            <h2 className="font-display font-bold text-lg">Deine nächsten Schritte</h2>
          </div>
          <p className="text-sm text-slate-600 mb-5 leading-relaxed">
            Du hast den RoboForex-Partneraccount beantragt — perfekt! So geht es chronologisch weiter:
          </p>

          <ol className="space-y-3">
            {/* Step 09 — Onboarding bestätigen */}
            <li data-testid="post-submit-step-9" className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
              <div className="flex items-start gap-4">
                <div className="shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 text-white font-mono-tab font-bold text-sm flex items-center justify-center shadow-sm">09</div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-slate-900 mb-1">Onboarding unten bestätigen</div>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    Wenn du alle Schritte oben abgehakt hast, bestätige dein Onboarding mit dem Button
                    am Ende dieser Seite.
                    <mark className="ml-1 bg-yellow-200 text-slate-900 font-semibold px-1 rounded">
                      Du erhältst dann per E-Mail den Link zu deinem Partner-Dashboard — inklusive
                      Anleitung, wie du die RoboForex-Mail beantwortest.
                    </mark>
                  </p>
                </div>
              </div>
            </li>

            {/* Step 10 — RoboForex-Mail beantworten & Freigabe */}
            <li data-testid="post-submit-step-10" className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
              <div className="flex items-start gap-4">
                <div className="shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 text-white font-mono-tab font-bold text-sm flex items-center justify-center shadow-sm">10</div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-slate-900 mb-1">RoboForex-Mail beantworten &amp; auf Freigabe warten</div>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    In den nächsten Tagen sendet dir RoboForex die Mail{" "}
                    <em>„Your partner programme at RoboForex"</em> — <strong>antworte darauf</strong> mit dem
                    Bestätigungstext aus unserer E-Mail (Schritt 09). Danach schaltet RoboForex deinen
                    Partneraccount innerhalb von 3–5 Werktagen frei.
                  </p>
                </div>
              </div>
            </li>

            {/* Step 11 — Drei Daten im Dashboard nachtragen */}
            <li data-testid="post-submit-step-11" className="rounded-xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-white p-4 sm:p-5">
              <div className="flex items-start gap-4">
                <div className="shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-white font-mono-tab font-bold text-sm flex items-center justify-center shadow-sm">11</div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-slate-900 mb-1">
                    Reflink, Affiliate-Code &amp; Partner-Account-Nummer im Dashboard eintragen
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed mb-3">
                    Sobald du die RoboForex-Bestätigung hast, generierst du deinen persönlichen{" "}
                    <strong>Reflink</strong> im RoboForex-Partner-Dashboard und trägst folgende drei Daten
                    in deinem Alphalane-Partner-Dashboard unter <strong>„Profil"</strong> ein:
                  </p>
                  <ul className="space-y-1.5 text-sm text-slate-800 mb-4 ml-1">
                    <li className="flex items-start gap-2">
                      <ClipboardCheck className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span><strong>Reflink</strong> (Video 1) — die vollständige RoboForex-Registrierungs-URL</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ClipboardCheck className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span><strong>Affiliate-Code</strong> — deine RoboForex Agent-ID (z. B. <code className="bg-slate-100 px-1 rounded font-mono-tab text-xs">zbtxc</code>)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ClipboardCheck className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span><strong>Partner-Account-Nummer</strong> — die 6–10-stellige Zahl, auf die deine Provisionen ausgezahlt werden</span>
                    </li>
                  </ul>
                  <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-[13px] text-amber-900 leading-relaxed mb-4">
                    Nach dem Login siehst du bei fehlenden Daten ein <strong>„Setup unvollständig"</strong>-Banner
                    im Partner-Dashboard mit einer Kurzanleitung — dort trägst du alles direkt nach.
                  </div>

                  {/* Video-Anleitung (YouTube Short, hochkant) */}
                  <div className="flex justify-center" data-testid="post-submit-step-11-video">
                    <div
                      className="relative w-full max-w-[320px] rounded-2xl overflow-hidden bg-slate-900 shadow-lg ring-1 ring-slate-200"
                      style={{ aspectRatio: "9 / 16" }}
                    >
                      <iframe
                        src="https://www.youtube.com/embed/0UPDAspheH8?rel=0&modestbranding=1&playsinline=1"
                        title="Partner-Setup Anleitung"
                        className="absolute inset-0 w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </div>
                  <p className="mt-2 text-center text-[11px] text-slate-500 leading-relaxed max-w-md mx-auto">
                    Kurzanleitung — so findest du deine drei RoboForex-Daten und trägst sie ein.
                  </p>
                </div>
              </div>
            </li>
          </ol>
        </section>

        {/* ── Onboarding bestätigen ─────────────────────────────────── */}
        <section className="al-card p-6 sm:p-8 mt-8 border-violet-200 bg-gradient-to-br from-violet-50/50 via-white to-indigo-50/40" data-testid="onboarding-confirm-section">
          {confirmedAt ? (
            <div className="text-center py-2" data-testid="onboarding-confirmed">
              <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 border border-emerald-300 flex items-center justify-center mb-4">
                <CheckCircle2 className="h-7 w-7 text-emerald-600" />
              </div>
              <h2 className="font-display font-bold text-xl sm:text-2xl text-slate-900">Onboarding bestätigt — du bist startklar! 🎉</h2>
              <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto leading-relaxed">
                Wir haben dir eine E-Mail mit deinen nächsten Schritten gesendet — inklusive der
                Anleitung, wie du die RoboForex-Mail beantwortest.
              </p>
              <a href="/partner/coaching" data-testid="onboarding-confirmed-dashboard-cta" className="al-cta mt-5 inline-flex">
                Zu deinem Partner-Dashboard <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          ) : (
            <div className="text-center py-2">
              <h2 className="font-display font-bold text-xl sm:text-2xl text-slate-900">Alles erledigt? Dann bestätige dein Onboarding.</h2>
              <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto leading-relaxed">
                Mit deiner Bestätigung senden wir dir per E-Mail den Link zu deinem{" "}
                <strong>Partner-Dashboard</strong> und die Anleitung, wie du die kommende RoboForex-Mail beantwortest.
              </p>
              {percent < 100 && (
                <p className="mt-3 text-[13px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-block" data-testid="onboarding-confirm-hint">
                  Du hast erst {completed} von {STEPS.length} Schritten abgehakt — bitte schliesse zuerst alle Schritte oben ab.
                </p>
              )}
              <div className="mt-5">
                <button
                  type="button"
                  onClick={confirmOnboarding}
                  disabled={confirming || percent < 100}
                  data-testid="onboarding-confirm-btn"
                  className="al-cta !px-8 !py-4 justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {confirming ? (<><Loader2 className="h-4 w-4 animate-spin" /> Wird bestätigt…</>) : (<>✅ Ich habe alle Onboarding-Schritte abgeschlossen</>)}
                </button>
              </div>
            </div>
          )}
        </section>
      </main>

      <footer className="border-t border-[var(--al-line)] mt-10">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6 text-[11px] text-slate-400 flex flex-wrap gap-4 items-center justify-between">
          <div>© Alphalane Trading Systems · Moonshine Solutions GmbH</div>
          <div className="flex gap-4">
            <Link to="/impressum" className="hover:text-slate-700">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-slate-700">Datenschutz</Link>
            <Link to="/agb" className="hover:text-slate-700">AGB</Link>
          </div>
        </div>
      </footer>

      <style>{`
        .al-input { width: 100%; padding: 12px 14px; border-radius: 12px; border: 1px solid var(--al-line); background: white; font-family: 'Manrope', sans-serif; font-size: 15px; color: var(--al-ink); transition: border-color 200ms, box-shadow 200ms; }
        .al-input:focus { outline: none; border-color: var(--al-purple); box-shadow: 0 0 0 4px rgba(139,92,246,0.12); }
        textarea.al-input { resize: vertical; min-height: 76px; }
      `}</style>
    </div>
  );
}

const Header = () => (
  <header className="border-b border-[var(--al-line)] bg-white">
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-4">
      <Link to="/" className="flex items-center gap-2.5 group">
        <img src={LOGO_URL} alt="Alphalane" className="h-9 w-auto" />
        <span className="hidden sm:inline font-display font-bold text-base text-slate-900 group-hover:text-[var(--al-purple)] transition-colors">
          Alphalane
        </span>
      </Link>
      <Link
        to="/"
        data-testid="partner-onboarding-back-home"
        className="text-xs sm:text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
      >
        <ChevronLeft className="h-4 w-4" /> Zurück zur Startseite
      </Link>
    </div>
  </header>
);

const Field = ({ icon, label, required, hint, children }) => (
  <label className="block">
    <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5">
      {icon} {label} {required && <span className="text-rose-500">*</span>}
    </span>
    {children}
    {hint && <span className="mt-1 block text-[11px] text-slate-400 leading-relaxed">{hint}</span>}
  </label>
);
